import axios from 'axios';
import { API_URL } from '../config/constants';
import { getTokenAuth } from '../utils/authUtils';
import { attachUnauthorizedInterceptor } from '../utils/authSessionManager';

const CATEGORY_CACHE_TTL = 5000;

const categoryReadCache = {
  all: { data: null, timestamp: 0, promise: null },
  list: { data: null, timestamp: 0, promise: null },
  adminAll: { data: null, timestamp: 0, promise: null },
  pending: { data: null, timestamp: 0, promise: null },
};

const invalidateCategoryReadCache = () => {
  Object.keys(categoryReadCache).forEach((key) => {
    categoryReadCache[key].data = null;
    categoryReadCache[key].timestamp = 0;
    categoryReadCache[key].promise = null;
  });
};

const getCachedOrFetch = async (cacheKey, fetcher) => {
  const cacheEntry = categoryReadCache[cacheKey];
  const now = Date.now();

  if (cacheEntry.data && now - cacheEntry.timestamp < CATEGORY_CACHE_TTL) {
    return cacheEntry.data;
  }

  if (cacheEntry.promise) {
    return cacheEntry.promise;
  }

  cacheEntry.promise = (async () => {
    try {
      const data = await fetcher();
      cacheEntry.data = data;
      cacheEntry.timestamp = Date.now();
      return data;
    } catch (error) {
      if (error?.response?.status === 429 && cacheEntry.data) {
        return cacheEntry.data;
      }
      throw error;
    } finally {
      cacheEntry.promise = null;
    }
  })();

  return cacheEntry.promise;
};

// Créer une instance axios avec le token d'authentification
const createAuthAxios = () => {
  const token = getTokenAuth();
  return attachUnauthorizedInterceptor(axios.create({
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    }
  }));
};

const categoryService = {
  // Récupérer toutes les catégories avec leur hiérarchie
  getAllCategories: async () => {
    try {
      return await getCachedOrFetch('all', async () => {
        const response = await axios.get(`${API_URL}/categories`);
        return response.data.categories;
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Récupérer toutes les catégories (alias pour getAllCategories)
  getCategories: async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      return response.data.categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Récupérer une liste plate de toutes les catégories
  getCategoriesList: async () => {
    try {
      return await getCachedOrFetch('list', async () => {
        const response = await axios.get(`${API_URL}/categories/list`);
        return response.data.categories;
      });
    } catch (error) {
      console.error('Error fetching categories list:', error);
      throw error;
    }
  },

  // Récupérer une catégorie par ID ou slug
  getCategory: async (idOrSlug) => {
    try {
      // Vérification de sécurité pour éviter les requêtes avec undefined
      if (!idOrSlug || idOrSlug === 'undefined' || idOrSlug === 'null') {
        console.error('getCategory called with invalid parameter:', idOrSlug);
        throw new Error('Invalid category ID or slug');
      }

      // Ajouter un délai aléatoire pour éviter les requêtes trop fréquentes
      await new Promise(resolve => setTimeout(resolve, Math.random() * 300));
      
      const response = await axios.get(`${API_URL}/categories/${idOrSlug}`);
      return response.data.category;
    } catch (error) {
      console.error(`Error fetching category ${idOrSlug}:`, error);
      throw error;
    }
  },

  // Rechercher des catégories
  searchCategories: async (query, limit = 10, offset = 0) => {
    try {
      const response = await axios.get(
        `${API_URL}/categories/search?q=${query}&limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      console.error('Error searching categories:', error);
      throw error;
    }
  },

  // Obtenir le chemin d'une catégorie (fil d'Ariane)
  getCategoryPath: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/categories/${id}/path`);
      return response.data.path;
    } catch (error) {
      console.error(`Error fetching category path for ${id}:`, error);
      throw error;
    }
  },

  // --- Méthodes nécessitant une authentification ---

  // Créer une nouvelle catégorie
  createCategory: async (categoryData) => {
    try {
      const authAxios = createAuthAxios();
      
      // Détecter si c'est FormData (pour les uploads d'images)
      if (categoryData instanceof FormData) {
        const token = getTokenAuth();
        const response = await axios.post(`${API_URL}/categories`, categoryData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
        });
        invalidateCategoryReadCache();
        return response.data.category;
      } else {
        // Données JSON normales
        const response = await authAxios.post(`${API_URL}/categories`, categoryData);
        invalidateCategoryReadCache();
        return response.data.category;
      }
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  // Mettre à jour une catégorie
  updateCategory: async (id, categoryData) => {
    try {
      const authAxios = createAuthAxios();
      
      // Détecter si c'est FormData (pour les uploads d'images)
      if (categoryData instanceof FormData) {
        const token = getTokenAuth();
        const response = await axios.patch(`${API_URL}/categories/${id}`, categoryData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
        });
        invalidateCategoryReadCache();
        return response.data.category;
      } else {
        // Données JSON normales
        const response = await authAxios.patch(`${API_URL}/categories/${id}`, categoryData);
        invalidateCategoryReadCache();
        return response.data.category;
      }
    } catch (error) {
      console.error(`Error updating category ${id}:`, error);
      throw error;
    }
  },

  // Télécharger une image pour une catégorie
  uploadCategoryImage: async (id, formData) => {
    try {
      const token = getTokenAuth();
      const response = await axios.patch(`${API_URL}/categories/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      return response.data.category;
    } catch (error) {
      console.error(`Error uploading image for category ${id}:`, error);
      throw error;
    }
  },

  // Supprimer une catégorie
  deleteCategory: async (id) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(`${API_URL}/categories/${id}`);
      invalidateCategoryReadCache();
      return response.data;
    } catch (error) {
      console.error(`Error deleting category ${id}:`, error);
      throw error;
    }
  },

  // Réorganiser les catégories
  reorderCategories: async (categoriesOrder) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.patch(`${API_URL}/categories/reorder`, {
        categories: categoriesOrder,
      });
      invalidateCategoryReadCache();
      return response.data;
    } catch (error) {
      console.error('Error reordering categories:', error);
      throw error;
    }
  },

  // Précharger le cache des catégories (admin)
  preloadCategoryCache: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(`${API_URL}/categories/preload-cache`);
      return response.data;
    } catch (error) {
      console.error('Error preloading category cache:', error);
      throw error;
    }
  },
  // Créer une nouvelle catégorie avec image
  createCategoryWithImage: async (formData) => {
    try {
      // Vérifier que formData est bien un FormData
      if (!(formData instanceof FormData)) {
        console.error('Expected FormData but got:', typeof formData);
        throw new Error('Format de données invalide pour l\'upload d\'image');
      }
      
      // Vérifier que le champ categoryImage est présent
      let hasImage = false;
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
        if (pair[0] === 'categoryImage') {
          hasImage = true;
        }
      }
      
      if (!hasImage) {
        console.error('No categoryImage field found in FormData');
        throw new Error('Aucune image trouvée dans les données');
      }
      
      const token = getTokenAuth();
      const response = await axios.post(`${API_URL}/categories`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data.category;
    } catch (error) {
      console.error('Error creating category with image:', error);
      throw error;
    }
  },

  // Vider le cache des catégories (admin)
  clearCategoryCache: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(`${API_URL}/categories/clear-cache`);
      invalidateCategoryReadCache();
      return response.data;
    } catch (error) {
      console.error('Error clearing category cache:', error);
      throw error;
    }
  },

  // Admin: Récupérer TOUTES les catégories incluant pending/rejected
  getAllCategoriesAdmin: async () => {
    try {
      return await getCachedOrFetch('adminAll', async () => {
        const authAxios = createAuthAxios();
        const response = await authAxios.get(`${API_URL}/categories/admin/all`);
        return response.data.categories;
      });
    } catch (error) {
      console.error('Error fetching all categories (admin):', error);
      throw error;
    }
  },

  // Admin: Récupérer les catégories en attente d'approbation
  getPendingCategories: async () => {
    try {
      return await getCachedOrFetch('pending', async () => {
        const authAxios = createAuthAxios();
        const response = await authAxios.get(`${API_URL}/categories/admin/pending`);
        return response.data.categories;
      });
    } catch (error) {
      console.error('Error fetching pending categories:', error);
      throw error;
    }
  },

  // Utilisateur connecté: récupérer ses demandes de catégories
  getMyCategoryRequests: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(`${API_URL}/categories/my-requests`);
      return response.data.categories || [];
    } catch (error) {
      console.error('Error fetching my category requests:', error);
      throw error;
    }
  },

  // Admin: Approuver une catégorie
  approveCategory: async (id) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(`${API_URL}/categories/${id}/approve`);
      invalidateCategoryReadCache();
      return response.data;
    } catch (error) {
      console.error('Error approving category:', error);
      throw error;
    }
  },

  // Admin: Rejeter une catégorie
  rejectCategory: async (id, reason) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(`${API_URL}/categories/${id}/reject`, { reason });
      invalidateCategoryReadCache();
      return response.data;
    } catch (error) {
      console.error('Error rejecting category:', error);
      throw error;
    }
  },

  // Admin: Archiver une catégorie
  archiveCategory: async (id) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.patch(`${API_URL}/categories/${id}`, { status: 'archived' });
      invalidateCategoryReadCache();
      return response.data;
    } catch (error) {
      console.error('Error archiving category:', error);
      throw error;
    }
  },
};

export default categoryService;